/**
 * Auth Service
 * Handles authentication API calls
 */

import { apiFetch, ApiError } from '@/lib/api'
import { useAuthStore, type User } from '@/stores/auth-store'
import { useTenantStore } from '@/stores/tenant-store'
import { collectClientDetails } from './clientDetails'

/**
 * Login request payload
 */
interface LoginRequest {
  email: string
  password: string
}

/**
 * Login API response structure
 */
interface LoginResponse {
  success: boolean
  message: string
  data: {
    accessToken: string
    refreshToken: string
    expiresIn: number
    tokenType: string
    user: User
  }
  timestamp: string
}

/**
 * Login result for UI consumption
 */
export interface LoginResult {
  success: boolean
  message: string
  user?: User
}

/**
 * Refresh API response structure — identical shape to login (the backend
 * returns the same AuthResponse DTO, with a ROTATED refreshToken).
 */
interface RefreshResponse {
  success: boolean
  message: string
  data: {
    accessToken: string
    refreshToken: string
    expiresIn: number
    tokenType: string
    user: User
  }
  timestamp: string
}

/**
 * Fresh tokens + user returned by a successful refresh.
 */
export interface RefreshedAuth {
  accessToken: string
  refreshToken: string
  expiresIn: number
  tokenType: string
  user: User
}

/**
 * Exchange a (rotating) refresh token for a fresh access token.
 *
 * The endpoint is PUBLIC (no Authorization header), so this uses `apiFetch`
 * directly. The backend rotates the refresh token — the caller MUST persist
 * the returned `refreshToken` for the next refresh.
 *
 * Returns `null` when the refresh token itself is rejected (HTTP 401 — expired
 * or revoked), signalling "session truly expired". Other (network/5xx) errors
 * propagate so the caller can decide.
 */
export async function refreshAccessToken(refreshToken: string): Promise<RefreshedAuth | null> {
  try {
    const response = await apiFetch<RefreshResponse>('/api/v1/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    })
    if (response.success && response.data) {
      return response.data
    }
    return null
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null
    }
    throw error
  }
}

/**
 * Authenticate user with email and password of the user
 */
export async function login(credentials: LoginRequest): Promise<LoginResult> {
  const { setAuth, setLoading } = useAuthStore.getState()

  setLoading(true)

  try {
    // `client` = what the browser reports about itself, for the backend's login record.
    const response = await apiFetch<LoginResponse>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ ...credentials, client: await collectClientDetails() }),
    })

    if (response.success && response.data) {
      const { user, accessToken, refreshToken, expiresIn, tokenType } = response.data

      // Store auth data
      setAuth(user, {
        accessToken,
        refreshToken,
        expiresIn,
        tokenType,
      })

      // Hydrate the tenant profile (currency/tax/country/locale) —
      // fire-and-forget; hooks fall back to platform defaults until it lands.
      void useTenantStore.getState().hydrate(accessToken)

      return {
        success: true,
        message: response.message,
        user,
      }
    }

    return {
      success: false,
      message: response.message || 'Login failed',
    }
  } catch (error) {
    setLoading(false)

    if (error instanceof ApiError) {
      // Handle specific error cases
      if (error.status === 401) {
        return {
          success: false,
          message: 'Invalid email or password',
        }
      }
      if (error.status === 403) {
        return {
          success: false,
          message: 'Account locked. Contact your administrator.',
        }
      }
      return {
        success: false,
        message: error.message,
      }
    }

    // Network or other errors
    return {
      success: false,
      message: 'Unable to connect. Please try again.',
    }
  }
}

/**
 * Logout user and clear auth state
 * Also clears all localStorage, sessionStorage, and cookies
 */
export function logout(): void {
  const { logout: clearAuth, tokens } = useAuthStore.getState()

  // Tell the server, so the session is closed (its refresh token revoked) and the
  // sign-out is recorded in login events. Fire-and-forget: signing out never waits on it.
  if (tokens?.refreshToken) {
    void apiFetch('/api/v1/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    }).catch(() => undefined)
  }

  clearAuth()
  useTenantStore.getState().reset()

  // Clear all localStorage
  localStorage.clear()

  // Clear all sessionStorage
  sessionStorage.clear()

  // Clear all cookies
  document.cookie.split(';').forEach((cookie) => {
    const eqPos = cookie.indexOf('=')
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim()
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
  })
}

/**
 * Check if current auth is valid
 * In a real app, this might validate the token with the server
 */
export function checkAuth(): boolean {
  const { isAuthenticated, tokens } = useAuthStore.getState()

  if (!isAuthenticated || !tokens) {
    return false
  }

  // Could add token expiry check here
  // For now, trust the stored state
  return true
}
